// Copyright (c) 2026 Vigeos SASU. Tous droits réservés.
import React, { useEffect, useState } from 'react';
import {
  Alert, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControl, InputLabel, MenuItem, Select, Table, TableBody, TableCell, TableHead,
  TableRow, TextField, Typography,
} from '@mui/material';
import api from '../../utils/api';
import { errorMessage } from '../../utils/erreurs';
import { NOT_PROVIDED } from '../../utils/formatFacturation';

/**
 * Import de chambres le jour de la pose, par le même code que la console du
 * client (`services/pro/import_chambres.py`) : mêmes en-têtes tolérés (chambre ou numéro,
 * unité ou étage, prénom, nom ; séparateur « ; », « , » ou tabulation), import
 * transactionnel vérifié d'abord en `dry_run`. Les résidents importés naissent
 * sans consentement : le recueil se fait dans la console, jamais par un import.
 * La trace part au journal d'administration, pas à celui de l'établissement.
 */
export default function RoomImportDialog({ ouvert: isOpen, sites, siteInitial, onFermer: onClose, onImporte: onImported }) {
  const [siteId, setSiteId] = useState('');
  const [csv, setCsv] = useState('');
  const [verification, setVerification] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [enCours, setEnCours] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    const activeItems = (sites || []).filter((s) => s.is_active !== false);
    setSiteId(siteInitial || (activeItems.length === 1 ? activeItems[0].id : ''));
    setCsv(''); setVerification(null); setResult(null); setError(''); setEnCours(false);
    // Seulement à l'ouverture : `sites` est un nouveau tableau à chaque
    // rechargement du parent, et en dépendre effacerait le CSV en cours de
    // saisie.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const site = (sites || []).find((s) => s.id === siteId);

  const readFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => { setCsv(String(reader.result || '')); setVerification(null); };
    reader.readAsText(f, 'utf-8');
    e.target.value = '';
  };

  const send = async (dryRun) => {
    if (!siteId || !csv.trim()) return;
    setEnCours(true); setError('');
    try {
      const r = await api.post(`/pro/admin/sites/${siteId}/rooms/import`, { csv, dry_run: dryRun });
      if (dryRun) {
        setVerification({ ...r.data, csv, siteId });
      } else {
        // La fiche ne se recharge qu'à la fermeture : la recharger ici
        // renouvellerait la liste des établissements et effacerait ce compte
        // rendu avant qu'on l'ait lu.
        setResult(r.data);
      }
    } catch (e) {
      setVerification(null);
      setError(errorMessage(e, 'Import refusé'));
    } finally {
      setEnCours(false);
    }
  };

  const handleClose = () => {
    if (result) onImported?.(result);
    onClose();
  };

  const checkedUpToDate = verification && verification.csv === csv && verification.siteId === siteId;

  return (
    <Dialog open={!!isOpen} onClose={() => !enCours && handleClose()} maxWidth="md" fullWidth>
      <DialogTitle>Importer des chambres{site ? ` : ${site.name}` : ''}</DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2, whiteSpace: 'pre-line' }}>{error}</Alert>}
        {result ? (
          <Alert severity="success">
            {result.rooms_created} chambre(s) et {result.residents_created} résident(s) créés
            sur {site?.name}. {result.consent_pending > 0
              ? `${result.consent_pending} consentement(s) à recueillir depuis la console du client : sans lui, la chambre n'est pas surveillée.`
              : ''}
          </Alert>
        ) : (
          <>
            <Alert severity="info" sx={{ mb: 2 }}>
              Une ligne par chambre. Colonnes reconnues : <code>chambre</code> (obligatoire),
              {' '}<code>unité</code> ou <code>étage</code>, <code>prénom</code>, <code>nom</code>.
              Une chambre qui existe déjà refuse tout le fichier. Résidents et
              consentements se corrigent ensuite depuis la console du client.
            </Alert>
            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
              <InputLabel>Établissement</InputLabel>
              <Select label="Établissement" value={siteId} disabled={enCours}
                onChange={(e) => { setSiteId(e.target.value); setVerification(null); }}>
                {(sites || []).map((s) => (
                  <MenuItem key={s.id} value={s.id} disabled={s.is_active === false}>
                    {s.name}{s.is_active === false ? ' (désactivé)' : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button size="small" component="label" sx={{ mb: 1 }} disabled={enCours}>
              Charger un fichier CSV
              <input type="file" hidden accept=".csv,.txt,text/csv,text/plain" onChange={readFile} />
            </Button>
            <TextField fullWidth multiline minRows={6} maxRows={16} size="small"
              label="Contenu CSV" value={csv} disabled={enCours}
              placeholder={'chambre;étage;prénom;nom\n101;1;Jeanne;Martin\n102;1;;'}
              onChange={(e) => { setCsv(e.target.value); }}
              inputProps={{ style: { fontFamily: 'monospace', fontSize: '0.8rem' } }} />

            {checkedUpToDate && (
              <>
                <Typography variant="body2" sx={{ mt: 2, mb: 1 }}>
                  Vérifié : <strong>{verification.rooms}</strong> chambre(s) et
                  {' '}<strong>{verification.residents}</strong> résident(s) seront créés sur
                  {' '}<strong>{site?.name}</strong>. Aperçu des premières lignes :
                </Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Chambre</TableCell>
                      <TableCell>Unité</TableCell>
                      <TableCell>Prénom</TableCell>
                      <TableCell>Nom</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(verification.preview || []).map((p) => (
                      <TableRow key={p.room_number}>
                        <TableCell>{p.room_number}</TableCell>
                        <TableCell>{p.unit || NOT_PROVIDED}</TableCell>
                        <TableCell>{p.first_name || NOT_PROVIDED}</TableCell>
                        <TableCell>{p.last_name || NOT_PROVIDED}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={enCours}>{result ? 'Fermer' : 'Annuler'}</Button>
        {!result && (
          <>
            <Button variant="outlined" onClick={() => send(true)}
              disabled={enCours || !siteId || !csv.trim()}>
              Vérifier
            </Button>
            <Button variant="contained" onClick={() => send(false)}
              disabled={enCours || !checkedUpToDate}>
              {enCours ? <CircularProgress size={18} /> : 'Importer'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
